import { baseApi } from "./baseApi";
import * as API from "@utils/apiPath";
import type {
  MenuApiResponse,
  MenuFormValues,
  MenuPayload,
  MenuPlacement,
  MenuRow,
} from "interface/cmsMenu";
import type { Pagination } from "interface/common";
import { LANGUAGE } from "@utils/constant/common";

type RawMenu = Record<string, unknown>;

function coercePagination(raw: unknown): Pagination {
  const p = raw as Record<string, unknown> | null | undefined;
  return {
    total: Number(p?.total ?? 0),
    page: Number(p?.page ?? 1),
    currentPage: Number(p?.currentPage ?? p?.page ?? 1),
    limit: Number(p?.limit ?? 10),
  };
}

function normalizeMenuTitle(raw: unknown): MenuRow["title"] {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return Object.fromEntries(
      Object.entries(raw as Record<string, unknown>).map(([k, v]) => [
        k,
        v == null ? "" : String(v),
      ]),
    );
  }
  if (typeof raw === "string" && raw.trim()) return { en: raw.trim() };
  return { [LANGUAGE]: "" };
}

function normalizeSegment(seg: string): MenuPlacement | null {
  const t = seg.trim().toLowerCase().replace(/[\s_]+/g, "");
  if (t === "footer") return "footer";
  if (t === "app") return "app";
  if (t === "header") return "header";
  return null;
}

/** Decode API placement (pipe-separated multi, or legacy Both / All / single) */
function normalizeMenuPlacement(raw: unknown): MenuPlacement[] {
  if (Array.isArray(raw)) {
    const set = new Set<MenuPlacement>();
    for (const x of raw) {
      const s = normalizeSegment(String(x));
      if (s) set.add(s);
    }
    return set.size ? Array.from(set).sort(sortPlacement) : ["header"];
  }
  const s = String(raw ?? "").trim();
  if (!s) return ["header"];
  const lower = s.toLowerCase();
  if (lower === "both") return ["header", "footer"];
  if (lower === "all") return ["header", "footer", "app"];
  if (s.includes("|")) {
    const set = new Set<MenuPlacement>();
    for (const part of s.split("|")) {
      const seg = normalizeSegment(part);
      if (seg) set.add(seg);
    }
    return set.size ? Array.from(set).sort(sortPlacement) : ["header"];
  }
  const one = normalizeSegment(s);
  return one ? [one] : ["header"];
}

function sortPlacement(a: MenuPlacement, b: MenuPlacement): number {
  const order: MenuPlacement[] = ["app", "footer", "header"];
  return order.indexOf(a) - order.indexOf(b);
}

function mapRawMenuToRow(raw: RawMenu): MenuRow {
  const id = String(raw.id ?? raw._id ?? "");
  return {
    id,
    title: normalizeMenuTitle(raw.title),
    placement: normalizeMenuPlacement(raw.placement),
    status: typeof raw.status === "boolean" ? raw.status : raw.status === true,
    createdAt: raw.createdAt != null ? String(raw.createdAt) : undefined,
    updatedAt: raw.updatedAt != null ? String(raw.updatedAt) : undefined,
  };
}

function normalizeMenuListResponse(payload: unknown): MenuApiResponse {
  const body = payload as {
    statusCode?: number;
    message?: string;
    data?: { results?: RawMenu[]; pagination?: unknown };
  };
  const results = (body.data?.results ?? []).map((r) => mapRawMenuToRow(r as RawMenu));
  return {
    statusCode: Number(body.statusCode ?? 200),
    message: String(body.message ?? ""),
    data: {
      results,
      pagination: coercePagination(body.data?.pagination),
    },
  };
}

function placementsToApi(placements: MenuPlacement[]): string[] {
  const map: Record<MenuPlacement, string> = {
    header: "Header",
    footer: "Footer",
    app: "App",
  };
  const unique = [...new Set(placements)].filter(Boolean);
  return unique.length ? unique.map((p) => map[p]) : ["Header"];
}

function menuFormToApiBody(params: MenuFormValues): Record<string, unknown> {
  const body: Record<string, unknown> = {
    title: { [LANGUAGE]: params.title.trim() },
    placement: placementsToApi(params.placement ?? ["header"]),
  };
  if (params.id) body.id = params.id;
  if (typeof params.status === "boolean") {
    body.status = params.status;
  }
  return body;
}

export const cmsMenuApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCmsMenus: builder.query<MenuApiResponse, { [key: string]: string | number }>({
      query: (params) => ({
        url: API.GET_CMS_MENU,
        method: "GET",
        params,
      }),
      transformResponse: normalizeMenuListResponse,
      providesTags: ["CmsMenu"],
    }),
    getCmsMenuList: builder.query<MenuApiResponse, { [key: string]: string | number } | void>({
      query: (params) => ({
        url: `${API.GET_CMS_MENU}/list`,
        method: "GET",
        params: params || undefined,
      }),
      transformResponse: normalizeMenuListResponse,
      providesTags: ["CmsMenu"],
    }),
    createCmsMenu: builder.mutation<MenuApiResponse, MenuPayload>({
      query: ({ params }) => ({
        url: API.GET_CMS_MENU,
        method: "POST",
        body: menuFormToApiBody(params),
      }),
      invalidatesTags: ["CmsMenu"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          arg.onClose();
        } catch (error) {
          console.error("Failed to create menu:", error);
        }
      },
    }),
    updateCmsMenu: builder.mutation<MenuApiResponse, MenuPayload>({
      query: ({ params }) => ({
        url: API.GET_CMS_MENU,
        method: "PUT",
        body: menuFormToApiBody(params),
      }),
      invalidatesTags: ["CmsMenu"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          arg.onClose();
        } catch (error) {
          console.error("Failed to update menu:", error);
        }
      },
    }),
    deleteCmsMenu: builder.mutation<MenuApiResponse, string>({
      query: (id) => ({
        url: `/${API.GET_CMS_MENU}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CmsMenu"],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          console.error("Failed to delete menu:", error);
        }
      },
    }),
    reorderCmsMenus: builder.mutation<MenuApiResponse, { ids: string[] }>({
      query: ({ ids }) => ({
        url: `/${API.GET_CMS_MENU}/reorder`,
        method: "PUT",
        body: { ids },
      }),
      invalidatesTags: ["CmsMenu"],
    }),
    bulkUpdateCmsMenuStatus: builder.mutation<
      MenuApiResponse,
      { ids: string[]; status: boolean }
    >({
      query: ({ ids, status }) => ({
        url: `/${API.GET_CMS_MENU}/bulk-status`,
        method: "PUT",
        body: { ids, status },
      }),
      invalidatesTags: ["CmsMenu"],
    }),
    bulkDeleteCmsMenus: builder.mutation<MenuApiResponse, { ids: string[] }>({
      query: ({ ids }) => ({
        url: `/${API.GET_CMS_MENU}/bulk-delete`,
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: ["CmsMenu"],
    }),
  }),
});

export const {
  useGetCmsMenusQuery,
  useGetCmsMenuListQuery,
  useCreateCmsMenuMutation,
  useUpdateCmsMenuMutation,
  useDeleteCmsMenuMutation,
  useReorderCmsMenusMutation,
  useBulkUpdateCmsMenuStatusMutation,
  useBulkDeleteCmsMenusMutation,
} = cmsMenuApi;
